import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useSpecificPayroll from '@ubeya/shared/hooks/account/useSpecificPayroll';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { EditableInputRenderer } from '@ubeya/shared-web/components';
import { isReadOnly } from 'shared/utils/timesheet';
import { PAYROLL_CUSTOM_FIELD_TYPE } from 'shared/constants/payroll';
import { PAYROLL_FIELDS_WITHOUT_CURRENCY } from 'payroll/constants';

const FieldRenderer = ({
  rowData,
  columnData: { fieldId, refetchPayroll, showTotal = false, readOnly: generalReadOnly = false, isClient = false }
}) => {
  const { payroll, payrollWithLabourCost, timesheetId, isJob, id, employeeSlotId, employee, isPto, isLeave } = rowData;
  const { currency } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();

  const field = useMemo(
    () => (payrollWithLabourCost || payroll)?.fields?.find?.((payrollField) => payrollField.id === fieldId),
    [fieldId, payroll, payrollWithLabourCost]
  );
  const fieldValue = formattingMap.number(showTotal ? field?.total ?? '' : field?.value ?? '');

  const [value, setValue] = useState(fieldValue);
  const [prevValue, setPrevValue] = useState(value);

  useEffect(() => {
    setValue(fieldValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  const {
    updateField: { mutateAsync: update, isLoading }
  } = useSpecificPayroll(refetchPayroll);

  const updateValue = useCallback(async () => {
    if (prevValue !== value) {
      try {
        await update({
          [isJob ? 'jobId' : 'employeeSlotId']: isJob ? id : employeeSlotId,
          payrollFieldId: fieldId,
          value: value || 0,
          timesheetId,
          employeeId: employee?.id,
          valueKey: isClient ? 'clientValue' : 'value'
        });
        setPrevValue(value);
      } catch (error) {
        setValue(prevValue || fieldValue); // fieldValue as default as prevValue is not present at first load
      }
    }
  }, [prevValue, value, update, isJob, id, employeeSlotId, fieldId, timesheetId, employee?.id, isClient, fieldValue]);

  if (!payroll || isPto || isLeave) {
    return <></>;
  }

  // Auto-fixed fields are read-only as they are calculated automatically
  const isAutoFixedField = field?.type === PAYROLL_CUSTOM_FIELD_TYPE.AUTO_FIXED;

  return (
    <EditableInputRenderer
      isLoading={isLoading}
      value={value}
      onChange={setValue}
      onSubmitChange={updateValue}
      prefix={
        PAYROLL_FIELDS_WITHOUT_CURRENCY.includes(fieldId)
          ? ''
          : field?.type === PAYROLL_CUSTOM_FIELD_TYPE.HOURLY_WAGE_PERCENTAGE
          ? '%'
          : currency
      }
      readOnly={isAutoFixedField || generalReadOnly || isReadOnly(rowData)}
      tooltip={field?.calculationString}
      fillWidth
    />
  );
};

export default FieldRenderer;
